import { faBarcodeRead } from '@fortawesome/pro-light-svg-icons/faBarcodeRead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { searchProduct } from '../../../../../purchase-orders/api/products-api';
import { messages } from './messages';
import styles from './product-search-input.styles.module.less';

import type { MessageDescriptor } from 'react-intl';
import type { ProductSearchInputProps, SearchProduct } from './types';

const MIN_SEARCH_TERM_LENGTH = 3;

export const ProductSearchInput = ({
  onProductSelect,
  disabled = false,
  warningMessage,
  infoMessage,
  showAddButton = false,
  isUomEnabled,
}: ProductSearchInputProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchProduct[] | undefined>(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState<MessageDescriptor | undefined>(undefined);
  const isSearchingRef = useRef(false);
  const intl = useIntl();

  const handleProductClick = useCallback(
    (product: SearchProduct) => {
      let enriched = product;
      if (isUomEnabled && product.unitsOfMeasure?.length) {
        const normalized = searchTerm.trim().toLowerCase();
        const matched = product.unitsOfMeasure.find(
          (u) =>
            (u.barcode != null && u.barcode.toLowerCase().includes(normalized)) ||
            (u.uomSku != null && u.uomSku.toLowerCase().includes(normalized)),
        );
        if (matched !== undefined) enriched = { ...product, matchedUomId: matched.productUnitOfMeasureId };
      }
      onProductSelect(enriched);
      setSearchTerm('');
      setSearchResults(undefined);
      setSearchMessage(undefined);
    },
    [onProductSelect, isUomEnabled, searchTerm],
  );

  const handleSearch = useCallback(async () => {
    if (isSearchingRef.current) return;

    const trimmed = searchTerm.trim();

    if (trimmed.length < MIN_SEARCH_TERM_LENGTH) {
      setSearchMessage(messages.searchTermTooShort);
      setSearchResults(undefined);
      return;
    }

    isSearchingRef.current = true;
    setIsSearching(true);

    try {
      const products = await searchProduct(trimmed, isUomEnabled ?? false);
      const hasResults = products?.length > 0;

      setSearchResults(hasResults ? products : undefined);
      setSearchMessage(hasResults ? undefined : messages.noProductFound);

      if (products?.length === 1) handleProductClick(products[0]);
    } catch {
      setSearchMessage(messages.searchError);
    } finally {
      isSearchingRef.current = false;
      setIsSearching(false);
    }
  }, [searchTerm, handleProductClick, isUomEnabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSearchResults(undefined);
    setSearchMessage(undefined);
  }, []);

  const shouldShowDropdown = (searchResults?.length ?? 0) > 0 || searchMessage;

  const inlineMessage = warningMessage ?? infoMessage;
  const getInlineMessageType = () => {
    if (warningMessage) return INLINE_MESSAGE_TYPES.warning;
    if (infoMessage) return INLINE_MESSAGE_TYPES.info;
    return undefined;
  };

  return (
    <div className={styles['product-search-container']}>
      {shouldShowDropdown ? (
        <div className={styles['search-results']}>
          <div className={styles['result-dropdown-menu']}>
            {searchMessage ? <Text className={styles['result-message']} textType={TextType.Paragraph} value={searchMessage} /> : null}
            {searchResults?.map((product) => (
              <div key={product.productId} className={styles['result-item']}>
                <DropdownMenuItem className={styles.row} onClick={() => handleProductClick(product)}>
                  <Text className={styles['item-name']} textType={TextType.Paragraph} value={product.name} />
                  <Text className={styles['item-sku']} textType={TextType.Caption} value={product.sku} />
                </DropdownMenuItem>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <FlexRow gap={Gap.Space8}>
        <div className={styles['input-wrapper']}>
          <Input
            className={styles['search-input']}
            disabled={disabled || isSearching}
            inlineMessage={inlineMessage}
            inlineMessageType={getInlineMessageType()}
            name='searchTerm'
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage(messages.productSearchPlaceholder)}
            type='text'
            value={searchTerm}
            version={version2}
          />
          <FontAwesomeIcon className={styles['scanner-icon']} icon={faBarcodeRead} />
        </div>
        {showAddButton ? (
          <Button
            className={styles['add-button']}
            disabled={disabled || isSearching}
            onClick={handleSearch}
            theme={THEME_CONSTANTS.secondary}
          >
            {intl.formatMessage(messages.addButton)}
          </Button>
        ) : null}
      </FlexRow>
      <Text className={styles['helper-text']} textType={TextType.Paragraph} value={messages.searchHelperText} />
    </div>
  );
};
